import React from 'react'
import { useSearchParams } from 'react-router-dom'
import { ArrowDownTrayIcon, PrinterIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { exportToCsv } from '../../utils/registryExport'
import { printKycRecord } from '../../utils/kycRecordPrint'
import PatronIdentityCell from '../compliance-safe/components/PatronIdentityCell'
import { resolvePatronIdentity, clubOrVisitorIdLine, subjectTypeLabel, subjectTypeBadgeClass } from '../../utils/patronIdentity'
import OcrExtractPanel from '../../components/OcrExtractPanel'
import HelpScreenButton from '../../components/help/HelpScreenButton'
import ScreenHelpPanel from '../../components/help/ScreenHelpPanel'
import { useFeatureFlags, usePatronLabels } from '../../contexts/FeatureFlagContext'

const KYC_REGISTRY_HELP_SCREEN_KEY = 'page:compliance-safe/kyc-registry'

const KYC_LICENCE_ATTACHMENTS = new Set([
  'id_front.jpg',
  'id_back.jpg',
  'patron_photo.jpg',
  'verified_faceprint.jpg',
])

function isLicenceKycAttachment(filename) {
  return KYC_LICENCE_ATTACHMENTS.has(String(filename || '').trim().toLowerCase())
}

function kycLicenceRetentionMessage(imageRetention, hasOcr) {
  if (imageRetention?.state === 'scheduled') {
    return 'Licence images are scheduled for removal per venue retention settings.'
  }
  if (
    imageRetention?.state === 'deleted' ||
    imageRetention?.kycLicenceAttachmentsPresent === false
  ) {
    if (hasOcr) {
      return 'Licence images removed per retention policy. Structured ID extract below is retained for audit.'
    }
    return 'Licence images removed per retention policy.'
  }
  return null
}

const PRIMARY_IMAGE_ORDER = ['id_front.jpg', 'patron_photo.jpg', 'id_back.jpg', 'verified_faceprint.jpg']

function pickPrimaryKycImageFilename(attachments = []) {
  const list = Array.isArray(attachments) ? attachments : []
  const lower = list.map((n) => String(n || '').trim().toLowerCase())
  for (const key of PRIMARY_IMAGE_ORDER) {
    const idx = lower.indexOf(key)
    if (idx >= 0) return list[idx]
  }
  return list.find((n) => isLicenceKycAttachment(n)) || null
}

function kycReasonBadgeClass(reason) {
  if (reason === 'PAYOUT') return 'bg-green-100 text-green-900 border-green-200'
  if (reason === 'THRESHOLD') return 'bg-yellow-100 text-yellow-900 border-yellow-200'
  return 'bg-blue-100 text-blue-900 border-blue-200'
}

function kycSummaryDisplayName(item) {
  const fromRow = String(item?.member_name || '').trim()
  if (fromRow) return fromRow
  let ocr = item?.ocr_data
  if (typeof ocr === 'string') {
    try {
      ocr = JSON.parse(ocr)
    } catch {
      ocr = null
    }
  }
  if (ocr && typeof ocr === 'object') {
    const fromOcr = [ocr.given_name, ocr.family_name].filter(Boolean).join(' ').trim()
    if (fromOcr) return fromOcr
  }
  return '—'
}

function kycAmlPepBadges(item) {
  if (item?.aml_check_id == null) {
    return <span className="text-gray-500">—</span>
  }
  const pep = item.pep_match === true || item.pep_match === 1
  const sanctions = item.sanctions_match === true || item.sanctions_match === 1
  if (!pep && !sanctions) {
    return (
      <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-green-100 text-green-800">
        Clear
      </span>
    )
  }
  return (
    <span className="inline-flex flex-wrap gap-1">
      {pep ? (
        <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-amber-100 text-amber-800">
          PEP
        </span>
      ) : null}
      {sanctions ? (
        <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-red-100 text-red-800">
          Sanctions
        </span>
      ) : null}
    </span>
  )
}

/** Payout slip / PO reference from notes or related case. */
function kycPayoutSlipRef(item) {
  const notes = String(item?.notes || '').trim()
  const poMatch = notes.match(/Payout:\s*(PO-[A-Za-z0-9-]+)/i)
  if (poMatch) return poMatch[1]
  if (/^payout:/i.test(notes)) {
    return notes.replace(/^payout:\s*/i, '').split(/\n/)[0].trim() || null
  }
  if (item?.related_case_id) return String(item.related_case_id).trim()
  return null
}

function kycNotesExcludingPayoutSlip(item) {
  const notes = String(item?.notes || '').trim()
  if (!notes) return ''
  const withoutFirst = notes.replace(/^Payout:\s*PO-[A-Za-z0-9-]+\s*/i, '').trim()
  return withoutFirst
}

// Image Preview Component
const ImagePreview = ({ kycId, filename, previewKey, imagePreviews, loadImagePreview, size = 'default' }) => {
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState(false)
  const isHero = size === 'hero'

  React.useEffect(() => {
    const loadPreview = async () => {
      if (!imagePreviews[previewKey] && !loading && !error) {
        setLoading(true)
        const url = await loadImagePreview(kycId, filename)
        setLoading(false)
        if (!url) setError(true)
      }
    }
    loadPreview()
  }, [kycId, filename, previewKey, imagePreviews, loadImagePreview, loading, error])

  const frameClass = isHero
    ? 'w-full aspect-[3/4] max-h-72 overflow-hidden rounded border border-gray-200 bg-gray-50 flex items-center justify-center'
    : size === 'summary'
      ? 'w-full max-w-[148px] aspect-[3/4] max-h-40 overflow-hidden rounded border border-gray-200 bg-gray-50 flex items-center justify-center mx-auto'
      : 'w-full h-36 overflow-y-auto border rounded bg-gray-50'

  if (loading) {
    return (
      <div className={frameClass}>
        <div className="text-xs text-gray-500">Loading preview...</div>
      </div>
    )
  }

  if (error || !imagePreviews[previewKey]) {
    return (
      <div className={frameClass}>
        <div className="text-xs text-gray-500 text-center px-2">Preview unavailable</div>
      </div>
    )
  }

  return (
    <div className={frameClass}>
      <img
        src={imagePreviews[previewKey]}
        alt={filename}
        className={isHero || size === 'summary' ? 'w-full h-full object-contain' : 'w-full h-auto object-contain'}
        onError={() => setError(true)}
        title={isHero ? filename : 'Scroll vertically if image is tall'}
      />
    </div>
  )
}

const PAGE_SIZE = 25

export default function KycRegistry() {
  const [searchParams] = useSearchParams()
  const yearFromUrl = searchParams.get('year') || ''
  const memberIdFromUrl = searchParams.get('member_id') || ''
  const kycIdFromUrl = searchParams.get('kyc_id') || ''
  const labels = usePatronLabels()
  const { isClub } = useFeatureFlags()
  const [items, setItems] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState(null)
  const [page, setPage] = React.useState(1)
  const [total, setTotal] = React.useState(0)
  const [filters, setFilters] = React.useState({
    member_id: memberIdFromUrl || '',
    visitor_id: '',
    subject_type: '',
    reason: '',
    search: '',
    venue_id: '',
    date_from: yearFromUrl ? `${yearFromUrl}-01-01` : '',
    date_to: yearFromUrl ? `${yearFromUrl}-12-31` : '',
  })
  const [venues, setVenues] = React.useState([])
  const [open, setOpen] = React.useState(false)
  const [helpPanelOpen, setHelpPanelOpen] = React.useState(false)
  const [form, setForm] = React.useState({ member_id:'', member_name:'', reason:'PAYOUT', venue_id:'', related_case_id:'', notes:'', payout_amount:'' })
  const [attachFor, setAttachFor] = React.useState(null)
  const [uploadFiles, setUploadFiles] = React.useState([])
  const [existingUploadFiles, setExistingUploadFiles] = React.useState([])
  const [imagePreviews, setImagePreviews] = React.useState({})
  const [purgingFile, setPurgingFile] = React.useState(null)
  const fileRef = React.useRef(null)
  const newEntryFileRef = React.useRef(null)
  const kycDeepLinkHandled = React.useRef('')

  const refreshAttachmentsForRow = React.useCallback(async (kycId) => {
    if (kycId == null) return
    try {
      const attachRes = await authFetch(getLocalApiUrl(`/api/kyc-registry/${kycId}/attachments`))
      if (!attachRes.ok) return
      const attachData = await attachRes.json()
      if (!attachData.success || !Array.isArray(attachData.data)) return
      const names = attachData.data.map((att) => att.file_name).filter(Boolean)
      setItems((prev) =>
        prev.map((item) =>
          item.id === kycId ? { ...item, attachments: names, attachment_count: names.length } : item
        )
      )
    } catch (_) {}
  }, [])

  const openAttachments = React.useCallback(
    async (kycId) => {
      setAttachFor(kycId)
      try {
        const r = await authFetch(getLocalApiUrl(`/api/kyc-registry/${encodeURIComponent(kycId)}`))
        const j = await r.json().catch(() => ({}))
        if (r.ok && j.success && j.data) {
          setItems((prev) =>
            prev.map((item) =>
              item.id === kycId
                ? {
                    ...item,
                    ...j.data,
                    attachments: Array.isArray(j.data.attachments) ? j.data.attachments : item.attachments,
                    attachment_count:
                      j.data.attachment_count != null
                        ? j.data.attachment_count
                        : Array.isArray(j.data.attachments)
                          ? j.data.attachments.length
                          : item.attachment_count,
                    imageRetention: j.data.imageRetention ?? item.imageRetention,
                  }
                : item
            )
          )
        }
      } catch (_) {
        /* keep list row */
      }
      refreshAttachmentsForRow(kycId)
    },
    [refreshAttachmentsForRow]
  )

  const load = React.useCallback(async()=>{
    try {
      setLoading(true); setError(null)
      const qs = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([,v])=>v)))
      qs.set('limit', String(PAGE_SIZE))
      qs.set('page', String(page))
      const r = await authFetch(getLocalApiUrl(`/api/kyc-registry?${qs}`))
      const j = await r.json()
      if (!j.success) throw new Error(j.error || 'Failed to load KYC registry')
      const apiData = (j.data || []).map((item) => ({
        ...item,
        attachments: Array.isArray(item.attachments) ? item.attachments : [],
        attachment_count:
          item.attachment_count != null
            ? Number(item.attachment_count)
            : Array.isArray(item.attachments)
              ? item.attachments.length
              : 0,
        payout_amount: item.payout_amount || null,
        notes: item.notes || '',
        member_name: item.member_name || '',
        related_case_id: item.related_case_id || null,
      }))

      setItems(apiData)
      setTotal(Number(j.total) || 0)
    } catch (e) { setError(e.message) } finally { setLoading(false) }
  }, [filters, page])

  React.useEffect(() => {
    if (yearFromUrl) {
      const from = `${yearFromUrl}-01-01`
      const to = `${yearFromUrl}-12-31`
      if (filters.date_from !== from || filters.date_to !== to) {
        setFilters((prev) => ({ ...prev, date_from: from, date_to: to }))
        setPage(1)
      }
    }
  }, [yearFromUrl])

  React.useEffect(() => {
    if (memberIdFromUrl && memberIdFromUrl !== filters.member_id) {
      setFilters((prev) => ({ ...prev, member_id: memberIdFromUrl, visitor_id: '', subject_type: 'member' }))
      setPage(1)
    }
  }, [memberIdFromUrl])

  React.useEffect(() => {
    const id = String(kycIdFromUrl || '').trim()
    if (!id || loading) return
    if (kycDeepLinkHandled.current === id) return
    const match = items.find((row) => String(row.id) === id)
    if (match) {
      kycDeepLinkHandled.current = id
      openAttachments(match.id)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const r = await authFetch(getLocalApiUrl(`/api/kyc-registry/${encodeURIComponent(id)}`))
        const j = await r.json().catch(() => ({}))
        if (cancelled || !r.ok || !j.success || !j.data) return
        const row = {
          ...j.data,
          attachments: Array.isArray(j.data.attachments) ? j.data.attachments : [],
          attachment_count:
            j.data.attachment_count != null
              ? Number(j.data.attachment_count)
              : Array.isArray(j.data.attachments)
                ? j.data.attachments.length
                : 0,
        }
        setItems((prev) => {
          if (prev.some((item) => String(item.id) === id)) return prev
          return [row, ...prev]
        })
        kycDeepLinkHandled.current = id
        openAttachments(row.id)
      } catch (_) {
        /* ignore deep-link failures */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [kycIdFromUrl, loading, items, openAttachments])

  const updateFilter = (patch) => {
    setFilters((prev) => ({ ...prev, ...patch }))
    setPage(1)
  }

  const updatePatronRef = (value) => {
    if (filters.subject_type === 'visitor') {
      updateFilter({ visitor_id: value, member_id: '' })
    } else {
      updateFilter({ member_id: value, visitor_id: '' })
    }
  }

  const patronRefValue = filters.subject_type === 'visitor' ? filters.visitor_id : filters.member_id

  const patronRefLabel = filters.subject_type === 'visitor'
    ? labels.visitorId
    : filters.subject_type === 'member'
      ? 'Member #'
      : `Member # / ${labels.visitorId}`

  React.useEffect(()=>{ load() }, [load])

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const rangeFrom = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const rangeTo = Math.min(page * PAGE_SIZE, total)

  const handleExport = () => {
    const cols = [
      {
        header: 'Patron ID',
        accessor: (r) => {
          const id = resolvePatronIdentity(r)
          return id.patron_id || ''
        },
      },
      {
        header: 'Master Profile ID',
        accessor: (r) => resolvePatronIdentity(r).master_profile_id || '',
      },
      {
        header: 'Subject',
        accessor: (r) => resolvePatronIdentity(r).subject_type || '',
      },
      {
        header: 'Club / Visitor ID',
        accessor: (r) => {
          const sec = clubOrVisitorIdLine(resolvePatronIdentity(r))
          return sec ? `${sec.label}: ${sec.value}` : ''
        },
      },
      { header: 'Patron Name', accessor: r => r.member_name || '' },
      { header: 'Reason', accessor: r => r.reason || '' },
      { header: 'Venue', accessor: r => (venues.find(v => String(v.id) === String(r.venue_id))?.name) || r.venue_id || '' },
      { header: 'Related Case', accessor: r => r.related_case_id || '' },
      { header: 'Performed', accessor: r => r.performed_at ? new Date(r.performed_at).toLocaleString() : '' },
      { header: 'Notes', accessor: r => r.notes || '' },
    ]
    const suffix = filters.date_from || filters.date_to
      ? `-${filters.date_from || 'start'}_to_${filters.date_to || 'end'}`
      : ''
    exportToCsv(items, cols, `kyc-registry${suffix}.csv`)
  }

  const loadVenues = React.useCallback(async () => {
    try {
      const r = await authFetch(getLocalApiUrl('/api/venues'))
      const j = await r.json()
      if (j.success && Array.isArray(j.data)) setVenues(j.data || [])
    } catch (e) { /* venues optional */ }
  }, [])
  React.useEffect(() => { loadVenues() }, [loadVenues])

  const create = async()=>{
    try {
      setLoading(true); setError(null)
      
      // Ensure payout_amount is properly formatted
      const formData = {
        ...form,
        venue_id: form.venue_id || null,
        payout_amount: form.payout_amount ? parseFloat(form.payout_amount) : null
      }
      
      console.log('Creating KYC entry with data:', formData)
      
      const r = await authFetch(getLocalApiUrl('/api/kyc-registry'), { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(formData) })
      const j = await r.json()
      
      console.log('Create KYC response:', j)
      
      if (!r.ok || !j.success) throw new Error(j.error || 'Failed to create KYC entry')
      
      // Upload files if any were selected
      if (uploadFiles.length > 0 && j.data?.id) {
        console.log(`Uploading ${uploadFiles.length} files for new KYC entry ${j.data.id}`)
        for (const file of uploadFiles) {
          try {
            console.log(`Uploading file: ${file.name}`)
            const fd = new FormData()
            fd.append('file', file)
            const uploadResponse = await authFetch(getLocalApiUrl(`/api/kyc-registry/${j.data.id}/attachments`), { method:'POST', body: fd })
            
            if (!uploadResponse.ok) {
              if (uploadResponse.status === 404) {
                throw new Error('KYC attachment upload is not available (404).')
              }
              throw new Error(`HTTP ${uploadResponse.status}: ${uploadResponse.statusText}`)
            }
            
            const uploadResult = await uploadResponse.json()
            if (!uploadResult.success) {
              throw new Error(uploadResult.error || `Failed to upload ${file.name}`)
            }
            console.log(`Successfully uploaded: ${file.name}`)
          } catch (uploadError) {
            console.warn('Failed to upload file:', file.name, uploadError)
            throw uploadError
          }
        }
      }
      
      setOpen(false)
      setForm({ member_id:'', member_name:'', reason:'PAYOUT', venue_id:'', related_case_id:'', notes:'', payout_amount:'' })
      setUploadFiles([])
      if (newEntryFileRef.current) newEntryFileRef.current.value = ''
      await load()
    } catch (e) { setError(e.message) } finally { setLoading(false) }
  }

  const upload = async(id)=>{
    try {
      if (!existingUploadFiles.length) {
        setError('No files selected for upload')
        return
      }
      
      console.log(`Uploading ${existingUploadFiles.length} files for KYC entry ${id}`)
      
      // Upload all selected files
      let successCount = 0
      for (const file of existingUploadFiles) {
        try {
          console.log(`Uploading file: ${file.name}`)
      const fd = new FormData()
          fd.append('file', file)
      const r = await authFetch(getLocalApiUrl(`/api/kyc-registry/${id}/attachments`), { method:'POST', body: fd })
          
          console.log(`Upload response status: ${r.status}`)
          
          if (!r.ok) {
            if (r.status === 404) {
              throw new Error('KYC attachment upload is not available (404).')
            }
            throw new Error(`HTTP ${r.status}: ${r.statusText}`)
          }
          
      const j = await r.json()
          console.log('Upload response:', j)
          
          if (!j.success) throw new Error(j.error || `Failed to upload ${file.name}`)
          successCount++
        } catch (uploadError) {
          console.error('Failed to upload file:', file.name, uploadError)
          setError(`Failed to upload ${file.name}: ${uploadError.message}`)
          return // Stop on first error
        }
      }
      
      if (successCount > 0) {
        console.log(`Successfully uploaded ${successCount} files`)
        // Data will be refreshed by the load() call below
      }
      
      // Clear the file input and close modal
      if (fileRef.current) fileRef.current.value = ''
      setExistingUploadFiles([])
      setAttachFor(null)
      await load()
    } catch (e) { 
      console.error('Upload error:', e)
      setError(e.message) 
    }
  }

  const handleNewEntryFileChange = (e) => {
    const files = Array.from(e.target.files || [])
    setUploadFiles(files)
  }

  const removeUploadFile = (index) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleExistingUploadFileChange = (e) => {
    const files = Array.from(e.target.files || [])
    setExistingUploadFiles(files)
  }

  const removeExistingUploadFile = (index) => {
    setExistingUploadFiles(prev => prev.filter((_, i) => i !== index))
    // Update the file input to reflect the change
    if (fileRef.current) {
      const dt = new DataTransfer()
      existingUploadFiles.forEach((file, i) => {
        if (i !== index) dt.items.add(file)
      })
      fileRef.current.files = dt.files
    }
  }

  const loadImagePreview = async (kycId, filename) => {
    const key = `${kycId}_${filename}`
    console.log(`loadImagePreview called for key: ${key}`)
    
    if (imagePreviews[key]) {
      console.log(`Preview already exists for ${key}: ${imagePreviews[key]}`)
      return imagePreviews[key] // Already loaded
    }
    
    try {
      const url = getLocalApiUrl(`/api/kyc-registry/${kycId}/attachments/${encodeURIComponent(filename)}`)
      console.log(`Fetching image from: ${url}`)
      
      const response = await authFetch(url)
      console.log(`Response status: ${response.status}, ok: ${response.ok}`)
      
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`)
      }
      
      const contentType = response.headers.get('content-type')
      console.log(`Content-Type: ${contentType}`)
      
      const blob = await response.blob()
      console.log(`Blob created: size=${blob.size}, type=${blob.type}`)
      
      const blobUrl = URL.createObjectURL(blob)
      console.log(`Blob URL created: ${blobUrl}`)
      
      setImagePreviews(prev => ({
        ...prev,
        [key]: blobUrl
      }))
      
      return blobUrl
    } catch (error) {
      console.error('Error loading image preview:', error)
      return null
    }
  }

  const viewAttachment = async (kycId, filename) => {
    try {
      console.log(`Viewing attachment: ${filename} for KYC ${kycId}`)
      const response = await authFetch(getLocalApiUrl(`/api/kyc-registry/${kycId}/attachments/${encodeURIComponent(filename)}`))
      
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`)
      }
      
      // Get the content type from response headers
      const contentType = response.headers.get('content-type') || 'application/octet-stream'
      console.log('File content type:', contentType)
      
      // Get the blob and create a URL for viewing
      const blob = await response.blob()
      console.log('Blob created:', blob.size, 'bytes, type:', blob.type)
      
      // Create blob with explicit type if needed
      const typedBlob = new Blob([blob], { type: contentType })
      const url = URL.createObjectURL(typedBlob)
      
      console.log('Opening URL:', url)
      
      // Open in new window/tab
      const newWindow = window.open(url, '_blank')
      
      // Check if popup was blocked
      if (!newWindow) {
        // Fallback: create a download link
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        link.target = '_blank'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
      
      // Clean up the URL after a longer delay to ensure it loads
      setTimeout(() => {
        URL.revokeObjectURL(url)
        console.log('Blob URL cleaned up')
      }, 5000)
    } catch (error) {
      console.error('Error viewing attachment:', error)
      setError(`Failed to view ${filename}: ${error.message}`)
    }
  }

  const purgeLicenceImage = async (kycId, fileName) => {
    const label = fileName || 'all licence images'
    if (!window.confirm(`Remove ${label} from storage? Structured OCR extract will be kept for audit.`)) {
      return
    }
    setPurgingFile(fileName || '*')
    setError(null)
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/kyc-registry/${encodeURIComponent(kycId)}/purge-images`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(fileName ? { file_name: fileName } : {}),
        }
      )
      const j = await res.json().catch(() => ({}))
      if (!res.ok || !j.success) throw new Error(j.error || 'Failed to remove image')

      setImagePreviews((prev) => {
        const next = { ...prev }
        const keys = fileName
          ? [`${kycId}_${fileName}`]
          : Object.keys(next).filter((k) => k.startsWith(`${kycId}_`))
        keys.forEach((key) => {
          if (next[key]) {
            URL.revokeObjectURL(next[key])
            delete next[key]
          }
        })
        return next
      })

      const attachments = j.data?.attachments ?? []
      const imageRetention = j.imageRetention || j.data?.imageRetention || null
      setItems((prev) =>
        prev.map((item) =>
          item.id === kycId
            ? {
                ...item,
                attachments,
                attachment_count: attachments.length,
                imageRetention,
              }
            : item
        )
      )
    } catch (err) {
      setError(err.message || 'Failed to remove image')
    } finally {
      setPurgingFile(null)
    }
  }

  const deleteAttachment = async (kycId, filename) => {
    if (!confirm(`Are you sure you want to delete "${filename}"?`)) {
      return
    }
    
    try {
      console.log(`Deleting attachment: ${filename} for KYC ${kycId}`)
      const response = await authFetch(getLocalApiUrl(`/api/kyc-registry/${kycId}/attachments/${encodeURIComponent(filename)}`), {
        method: 'DELETE'
      })
      
      if (!response.ok) {
        throw new Error(`Failed to delete file: ${response.status} ${response.statusText}`)
      }
      
      const result = await response.json()
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete file')
      }
      
      console.log(`Successfully deleted: ${filename}`)
      
      // Refresh the data to update the UI
      await load()
    } catch (error) {
      console.error('Error deleting attachment:', error)
      setError(`Failed to delete ${filename}: ${error.message}`)
    }
  }

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      <div className="card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold">KYC Registry</h1>
            <div className="text-xs text-gray-500 mt-0.5">KYC performed due to payout or other triggers. 7-year retention for reporting.</div>
          </div>
          <HelpScreenButton
            screenKey={KYC_REGISTRY_HELP_SCREEN_KEY}
            onClick={() => setHelpPanelOpen(true)}
          />
        </div>

        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Patron type</label>
            <select
              className="select-field w-full"
              value={filters.subject_type}
              onChange={(e) => {
                const subject_type = e.target.value
                if (subject_type === 'visitor') {
                  updateFilter({ subject_type, member_id: '', visitor_id: filters.member_id })
                } else if (subject_type === 'member') {
                  updateFilter({ subject_type, visitor_id: '', member_id: filters.visitor_id })
                } else {
                  updateFilter({ subject_type })
                }
              }}
            >
              <option value="">All</option>
              <option value="member">Member</option>
              <option value="visitor">{labels.visitor}</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">{patronRefLabel}</label>
            <input
              className="input-field w-full"
              value={patronRefValue}
              onChange={(e) => updatePatronRef(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Reason</label>
            <select className="select-field w-full" value={filters.reason} onChange={(e)=>updateFilter({ reason: e.target.value })}>
              <option value="">All</option>
              <option value="PAYOUT">Payout</option>
              <option value="THRESHOLD">Threshold</option>
              <option value="MANUAL">Manual</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Venue</label>
            <select className="select-field w-full" value={filters.venue_id} onChange={(e)=>updateFilter({ venue_id: e.target.value })}>
              <option value="">All Venues</option>
              {venues.map(v => (
                <option key={v.id} value={v.id}>{v.name} ({v.code})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">From date</label>
            <input
              type="date"
              className="input-field w-full"
              value={filters.date_from}
              onChange={(e) => updateFilter({ date_from: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">To date</label>
            <input
              type="date"
              className="input-field w-full"
              value={filters.date_to}
              min={filters.date_from || undefined}
              onChange={(e) => updateFilter({ date_to: e.target.value })}
            />
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className="flex-1 min-w-[200px]">
            <label className="sr-only">Search</label>
            <input
              className="input-field w-full"
              placeholder="Search member name, case id, notes…"
              value={filters.search}
              onChange={(e)=>updateFilter({ search: e.target.value })}
            />
          </div>
          <button className="btn-primary shrink-0" onClick={()=>setOpen(true)}>New KYC Entry</button>
          <button
            type="button"
            onClick={handleExport}
            className="btn-secondary flex items-center gap-1.5 px-3 py-1.5 text-sm shrink-0 ml-auto"
            disabled={loading || items.length === 0}
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      <div className="card overflow-x-auto">
        <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-3 py-2 text-sm text-gray-600">
          <span>{loading ? 'Loading…' : `${total} entr${total === 1 ? 'y' : 'ies'}`}</span>
        </div>
        {error && <div className="text-sm text-red-600 mb-2 px-3 pt-2">{error}</div>}
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left">Performed</th>
              <th className="px-3 py-2 text-left">Patron</th>
              <th className="px-3 py-2 text-left">Reason</th>
              <th className="px-3 py-2 text-left">Amount</th>
              <th className="px-3 py-2 text-left">AML/PEP</th>
              <th className="px-3 py-2 text-left">Notes</th>
              <th className="px-3 py-2 text-left">Attachments</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {items.map(row => (
              <tr key={row.id}>
                <td className="px-3 py-2">{new Date(row.performed_at).toLocaleString()}<div className="text-xs text-gray-500">{row.performed_by}</div></td>
                <td className="px-3 py-2">
                  <PatronIdentityCell row={row} />
                </td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    row.reason === 'PAYOUT' ? 'bg-green-100 text-green-800' :
                    row.reason === 'THRESHOLD' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {row.reason}
                  </span>
                </td>
                <td className="px-3 py-2">
                  {row.payout_amount ? (
                    <span className="font-semibold text-green-600">
                      ${row.payout_amount.toLocaleString()}
                    </span>
                  ) : '—'}
                </td>
                <td className="px-3 py-2">
                  {row.aml_check_id != null ? (
                    <div className="flex flex-wrap gap-1">
                      {(row.pep_match === true || row.pep_match === 1) && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-800" title="PEP match">PEP</span>
                      )}
                      {(row.sanctions_match === true || row.sanctions_match === 1) && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800" title="Sanctions match">Sanctions</span>
                      )}
                      {(!row.pep_match || row.pep_match === 0) && (!row.sanctions_match || row.sanctions_match === 0) && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800">Clear</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-400 text-xs">—</span>
                  )}
                </td>
                <td className="px-3 py-2 max-w-xs">
                  <div className="truncate" title={row.notes}>
                    {row.notes || '—'}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap items-center gap-1">
                    <span className={`text-xs px-2 py-1 rounded ${(row.attachment_count || row.attachments?.length) ? 'bg-gray-100' : 'bg-gray-50 text-gray-500'}`}>
                      {(row.attachment_count || row.attachments?.length)
                        ? `${row.attachment_count || row.attachments.length} file${(row.attachment_count || row.attachments.length) > 1 ? 's' : ''}`
                        : 'No files'}
                    </span>
                    <button type="button" className="btn-secondary text-xs" onClick={() => openAttachments(row.id)}>View</button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length===0 && (
              <tr><td className="px-3 py-4 text-gray-500" colSpan={7}>No entries</td></tr>
            )}
          </tbody>
        </table>
        {!loading && total > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-3 py-3 text-sm text-gray-600">
            <p>
              Showing {rangeFrom}–{rangeTo} of {total}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              >
                Previous
              </button>
              <span className="text-xs text-gray-500">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4" onClick={()=>setOpen(false)}>
          <div className="w-full max-w-xl bg-white rounded shadow-lg" onClick={(e)=>e.stopPropagation()}>
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <div className="text-lg font-semibold">New KYC Entry</div>
              <button className="btn-secondary" onClick={()=>setOpen(false)}>Close</button>
            </div>
            <div className="p-4 grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Member #</label>
                <input className="input-field w-full" value={form.member_id} onChange={(e)=>setForm(p=>({...p, member_id:e.target.value}))} />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Member Name</label>
                <input className="input-field w-full" value={form.member_name} onChange={(e)=>setForm(p=>({...p, member_name:e.target.value}))} />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Reason</label>
                <select className="select-field w-full" value={form.reason} onChange={(e)=>setForm(p=>({...p, reason:e.target.value}))}>
                  <option value="PAYOUT">Payout</option>
                  <option value="THRESHOLD">Threshold</option>
                  <option value="MANUAL">Manual</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Venue</label>
                <select className="select-field w-full" value={form.venue_id} onChange={(e)=>setForm(p=>({...p, venue_id:e.target.value}))}>
                  <option value="">— Select Venue —</option>
                  {venues.map(v => (
                    <option key={v.id} value={v.id}>{v.name} ({v.code})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Payout Amount ($)</label>
                <input 
                  type="number" 
                  className="input-field w-full" 
                  value={form.payout_amount} 
                  onChange={(e)=>setForm(p=>({...p, payout_amount:e.target.value}))}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  onBlur={(e) => {
                    // Ensure the value is properly formatted when user leaves the field
                    const value = parseFloat(e.target.value)
                    if (!isNaN(value)) {
                      setForm(p=>({...p, payout_amount: value.toString()}))
                    }
                  }}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Related Case ID</label>
                <input className="input-field w-full" value={form.related_case_id} onChange={(e)=>setForm(p=>({...p, related_case_id:e.target.value}))} />
              </div>
              <div className="col-span-3">
                <label className="block text-xs text-gray-600 mb-1">Notes</label>
                <textarea className="input-field w-full" rows={3} value={form.notes} onChange={(e)=>setForm(p=>({...p, notes:e.target.value}))} />
              </div>
              <div className="col-span-3">
                <label className="block text-xs text-gray-600 mb-1">Attachments</label>
                <input 
                  type="file" 
                  ref={newEntryFileRef}
                  multiple
                  className="input-field w-full" 
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={handleNewEntryFileChange}
                />
                {uploadFiles.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-600 mb-2">Selected files:</p>
                    <div className="space-y-1">
                      {uploadFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 px-2 py-1 rounded text-xs">
                          <span className="truncate">{file.name}</span>
                          <button 
                            type="button"
                            onClick={() => removeUploadFile(index)}
                            className="text-red-600 hover:text-red-800 ml-2"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="px-4 py-3 border-t flex items-center justify-end gap-2">
              <button className="btn-secondary" onClick={()=>{
                setOpen(false)
                setForm({ member_id:'', member_name:'', reason:'PAYOUT', related_case_id:'', notes:'', payout_amount:'' })
                setUploadFiles([])
                if (newEntryFileRef.current) newEntryFileRef.current.value = ''
              }}>Cancel</button>
              <button className="btn-primary" onClick={create}>
                {loading ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {attachFor && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4" onClick={()=>{
          setAttachFor(null)
          setExistingUploadFiles([])
          // Clean up image previews for this KYC entry
          const kycPreviews = Object.keys(imagePreviews).filter(key => key.startsWith(`${attachFor}_`))
          kycPreviews.forEach(key => {
            if (imagePreviews[key]) {
              URL.revokeObjectURL(imagePreviews[key])
            }
          })
          setImagePreviews(prev => {
            const newPreviews = { ...prev }
            kycPreviews.forEach(key => delete newPreviews[key])
            return newPreviews
          })
          if (fileRef.current) fileRef.current.value = ''
        }}>
          <div className="w-full max-w-[58rem] bg-white rounded shadow-lg max-h-[92vh] overflow-y-auto" onClick={(e)=>e.stopPropagation()}>
            <div className="px-3 py-2 border-b flex items-center justify-between gap-3 sticky top-0 bg-white z-10">
              <div className="text-base font-semibold min-w-0 truncate">
                KYC record — {items.find(i => i.id === attachFor)?.member_id}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {(() => {
                  const item = items.find(i => i.id === attachFor)
                  if (!item) return null
                  const venueLabel =
                    venues.find(v => String(v.id) === String(item.venue_id))?.name || item.venue_id || '—'
                  return (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                      title="Print KYC record"
                      onClick={() =>
                        printKycRecord(item, {
                          venueLabel,
                          imagePreviews,
                          isClub,
                          retentionMessage: kycLicenceRetentionMessage(item.imageRetention, !!item.ocr_data),
                          onError: (msg) => toast.error(msg || 'Print failed'),
                        })
                      }
                    >
                      <PrinterIcon className="h-4 w-4" />
                      Print
                    </button>
                  )
                })()}
                <button className="btn-secondary" onClick={()=>{
                setAttachFor(null)
                setExistingUploadFiles([])
                // Clean up image previews for this KYC entry
                const kycPreviews = Object.keys(imagePreviews).filter(key => key.startsWith(`${attachFor}_`))
                kycPreviews.forEach(key => {
                  if (imagePreviews[key]) {
                    URL.revokeObjectURL(imagePreviews[key])
                  }
                })
                setImagePreviews(prev => {
                  const newPreviews = { ...prev }
                  kycPreviews.forEach(key => delete newPreviews[key])
                  return newPreviews
                })
                if (fileRef.current) fileRef.current.value = ''
              }}>Close</button>
              </div>
            </div>
            <div className="p-3">
              {(() => {
                const item = items.find(i => i.id === attachFor)
                if (!item) return null
                const venueLabel =
                  (venues.find(v => String(v.id) === String(item.venue_id))?.name) || item.venue_id || '—'
                const retentionMsg = kycLicenceRetentionMessage(item.imageRetention, !!item.ocr_data)
                const attachments = item.attachments || []
                const primaryImage = pickPrimaryKycImageFilename(attachments)
                const otherAttachments = primaryImage
                  ? attachments.filter((f) => f !== primaryImage)
                  : attachments
                const performedLabel = item.performed_at
                  ? new Date(item.performed_at).toLocaleString()
                  : '—'
                const summaryName = kycSummaryDisplayName(item)
                const patronId = resolvePatronIdentity(item)
                const clubLine = clubOrVisitorIdLine(patronId, isClub)
                const payoutSlip = kycPayoutSlipRef(item)
                const extraNotes = kycNotesExcludingPayoutSlip(item)

                const primaryImageSlot = (
                  <div className="relative group w-full max-w-[148px] mx-auto lg:mx-0">
                    {primaryImage ? (
                      <>
                        {isLicenceKycAttachment(primaryImage) && (
                          <div className="absolute inset-0 z-10 flex items-center justify-center rounded bg-black/0 opacity-0 transition group-hover:bg-black/40 group-hover:opacity-100">
                            <button
                              type="button"
                              className="btn-secondary text-xs bg-white shadow-sm disabled:opacity-50"
                              disabled={!!purgingFile}
                              onClick={() => purgeLicenceImage(attachFor, primaryImage)}
                            >
                              {purgingFile === primaryImage || purgingFile === '*'
                                ? 'Removing…'
                                : 'Remove image'}
                            </button>
                          </div>
                        )}
                        <ImagePreview
                          kycId={attachFor}
                          filename={primaryImage}
                          previewKey={`${attachFor}_${primaryImage}`}
                          imagePreviews={imagePreviews}
                          loadImagePreview={loadImagePreview}
                          size="summary"
                        />
                      </>
                    ) : (
                      <div className="aspect-[3/4] max-h-40 w-full max-w-[148px] mx-auto lg:mx-0 rounded border border-dashed border-gray-300 bg-white flex flex-col items-center justify-center text-center px-2 py-3">
                        <span className="text-xl mb-1 opacity-40" aria-hidden>
                          🪪
                        </span>
                        <p className="text-[10px] font-medium text-gray-500 leading-tight">No ID image</p>
                      </div>
                    )}
                  </div>
                )

                return (
                  <>
                    <div className="mb-3 rounded-lg border border-gray-300 bg-gradient-to-r from-gray-50 to-white p-3 shadow-sm">
                      {retentionMsg ? (
                        <div
                          className="mb-3 rounded-md border border-teal-200 bg-teal-50 px-2.5 py-1.5 text-[11px] text-teal-900"
                          role="status"
                        >
                          {retentionMsg}
                        </div>
                      ) : null}

                      <div className="grid grid-cols-1 md:grid-cols-[minmax(9rem,148px)_minmax(0,1fr)_minmax(0,1.15fr)] gap-4 items-start">
                        <div className="shrink-0">{primaryImageSlot}</div>

                        <div className="min-w-0 flex flex-col justify-center py-0.5">
                          <h2 className="text-lg font-bold text-gray-900 leading-tight">{summaryName}</h2>
                          <time className="mt-1.5 text-sm font-semibold text-gray-700 tabular-nums block">
                            {performedLabel}
                          </time>
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <span
                              className={`inline-flex px-2.5 py-0.5 text-xs font-bold rounded-full border ${kycReasonBadgeClass(item.reason)}`}
                            >
                              {item.reason || 'KYC'}
                            </span>
                            {item.payout_amount != null && item.payout_amount !== '' ? (
                              <span className="text-sm font-semibold text-emerald-700">
                                ${Number(item.payout_amount).toLocaleString()}
                              </span>
                            ) : null}
                          </div>
                          <div className="mt-2.5 flex items-center gap-2 flex-wrap">
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                              AML / PEP
                            </span>
                            {kycAmlPepBadges(item)}
                          </div>
                        </div>

                        <div className="min-w-0 border-t md:border-t-0 md:border-l border-gray-200 pt-3 md:pt-0 md:pl-4 text-xs text-gray-700 space-y-1.5">
                          <div>
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 block mb-0.5">
                              Payout slip
                            </span>
                            <span className="font-mono text-sm font-semibold text-gray-900 break-all">
                              {payoutSlip || '—'}
                            </span>
                          </div>
                          {extraNotes ? (
                            <div>
                              <span className="text-gray-400">Notes:</span>{' '}
                              <span className="whitespace-pre-wrap">{extraNotes}</span>
                            </div>
                          ) : null}
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span
                              className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${subjectTypeBadgeClass(patronId.subject_type)}`}
                            >
                              {subjectTypeLabel(patronId.subject_type, isClub)}
                            </span>
                            <span className="font-mono text-sm text-gray-900">{patronId.patron_id || '—'}</span>
                          </div>
                          {patronId.master_profile_id && patronId.patron_id !== patronId.master_profile_id ? (
                            <div className="font-mono text-gray-500 break-all">
                              Master profile {patronId.master_profile_id}
                            </div>
                          ) : null}
                          {patronId.profilesafe_profile_id ? (
                            <div className="font-mono text-gray-500 break-all">
                              Profile {patronId.profilesafe_profile_id}
                            </div>
                          ) : null}
                          {clubLine ? (
                            <div>
                              <span className="text-gray-400">{clubLine.label}:</span>{' '}
                              <span className="font-mono font-medium">{clubLine.value}</span>
                            </div>
                          ) : null}
                          <div>
                            <span className="text-gray-400">Venue:</span>{' '}
                            <span className="font-medium">{venueLabel}</span>
                          </div>
                          {item.performed_by ? (
                            <div>
                              <span className="text-gray-400">Performed by:</span>{' '}
                              <span>{item.performed_by}</span>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <OcrExtractPanel ocrData={item.ocr_data} columns={2} />
                    </div>

                    {otherAttachments.length > 0 ? (
                      <div className="mb-3">
                        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">
                          Other files
                        </h4>
                        {!retentionMsg && attachments.some(isLicenceKycAttachment) ? (
                          <p className="mb-2 text-[11px] text-gray-500">
                            Hover a licence image and use Remove image to purge the file from storage (OCR extract is
                            kept).
                          </p>
                        ) : null}
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                          {otherAttachments.map((filename, idx) => {
                            const isImage = filename.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/i)
                            const isLicence = isLicenceKycAttachment(filename)
                            const previewKey = `${attachFor}_${filename}`
                            const isPurging = purgingFile === filename || purgingFile === '*'

                            return (
                              <div key={idx} className="border border-gray-200 rounded-lg p-2 bg-white">
                                <div className="flex items-start justify-between mb-1.5 gap-1">
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center flex-shrink-0">
                                      {filename.toLowerCase().includes('.pdf')
                                        ? '📄'
                                        : isImage
                                          ? '🖼️'
                                          : '📎'}
                                    </div>
                                    <span className="text-sm truncate" title={filename}>
                                      {filename}
                                    </span>
                                  </div>
                                  <div className="flex gap-1 flex-shrink-0">
                                    <button
                                      type="button"
                                      className="btn-secondary text-xs"
                                      onClick={() => viewAttachment(attachFor, filename)}
                                    >
                                      Open
                                    </button>
                                    {!isLicence && (
                                      <button
                                        type="button"
                                        className="btn-secondary text-xs text-red-600"
                                        onClick={() => deleteAttachment(attachFor, filename)}
                                      >
                                        Delete
                                      </button>
                                    )}
                                  </div>
                                </div>
                                {isImage && (
                                  <div className="relative group mt-2">
                                    {isLicence && (
                                      <div className="absolute inset-0 z-10 flex items-center justify-center rounded bg-black/0 opacity-0 transition group-hover:bg-black/40 group-hover:opacity-100">
                                        <button
                                          type="button"
                                          className="btn-secondary text-xs bg-white shadow-sm disabled:opacity-50"
                                          disabled={!!purgingFile}
                                          onClick={() => purgeLicenceImage(attachFor, filename)}
                                        >
                                          {isPurging ? 'Removing…' : 'Remove image'}
                                        </button>
                                      </div>
                                    )}
                                    <ImagePreview
                                      kycId={attachFor}
                                      filename={filename}
                                      previewKey={previewKey}
                                      imagePreviews={imagePreviews}
                                      loadImagePreview={loadImagePreview}
                                    />
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                        <hr className="my-3" />
                      </div>
                    ) : !primaryImage && !retentionMsg ? (
                      <div className="mb-3 text-sm text-gray-500">
                        No attachments yet — use the section below to upload files.
                      </div>
                    ) : null}
                  </>
                )
              })()}

              {/* Upload new file */}
              <div className="mt-2 pt-2 border-t border-gray-100">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">Add file</h4>
                <input 
                  type="file" 
                  ref={fileRef} 
                  multiple 
                  className="input-field w-full" 
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={handleExistingUploadFileChange}
                />
                {existingUploadFiles.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-600 mb-2">Selected files:</p>
                    <div className="space-y-1">
                      {existingUploadFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 px-2 py-1 rounded text-xs">
                          <span className="truncate">{file.name}</span>
                          <button 
                            type="button"
                            onClick={() => removeExistingUploadFile(index)}
                            className="text-red-600 hover:text-red-800 ml-2"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              <div className="mt-3 flex items-center justify-end">
                  <button className="btn-primary" onClick={()=>upload(attachFor)}>
                    Upload {existingUploadFiles.length > 0 ? `${existingUploadFiles.length} File${existingUploadFiles.length > 1 ? 's' : ''}` : 'Files'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <ScreenHelpPanel
        screenKey={KYC_REGISTRY_HELP_SCREEN_KEY}
        open={helpPanelOpen}
        onClose={() => setHelpPanelOpen(false)}
        sectionIdPrefix="kyc-registry-help"
      />
    </div>
  )
}


